import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Auth, idToken, user } from '@angular/fire/auth';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { Observable, firstValueFrom, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import type { User } from '../../main/users/user.model';

export interface CompanyRef {
  companyId: string;
  companySlug: string;
}

/** The logged-in user: company from the ID token's custom claims, profile from users/{uid}. */
@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  readonly #auth = inject(Auth);
  readonly #firestore = inject(Firestore);

  // idToken emits on sign-in, sign-out and every token refresh, so new claims are picked up automatically
  readonly company$: Observable<CompanyRef | undefined> = idToken(this.#auth).pipe(
    switchMap(async () => (await this.#auth.currentUser?.getIdTokenResult())?.claims),
    map(toCompanyRef),
    distinctUntilChanged((a, b) => a?.companyId === b?.companyId && a?.companySlug === b?.companySlug),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly company = toSignal(this.company$);

  /**
   * Logged-in user's users/{uid} document, live (one shared listener; undefined when signed out).
   * Firestore, not the Auth profile: names and avatars are edited only there.
   */
  readonly profile = toSignal(
    user(this.#auth).pipe(
      switchMap((authUser) =>
        authUser
          ? (docData(doc(this.#firestore, 'users', authUser.uid), { idField: 'id' }) as Observable<User | undefined>)
          : of(undefined),
      ),
    ),
  );

  /** Waits for the token and returns its company, throws if the user has none. */
  async requireCompany(): Promise<CompanyRef> {
    const company = await firstValueFrom(this.company$);
    if (!company) {
      throw new Error('Current user has no company (companyId / companySlug claims missing, run sync-claims.js)');
    }
    return company;
  }
}

function toCompanyRef(claims: Record<string, unknown> | undefined): CompanyRef | undefined {
  const { companyId, companySlug } = claims ?? {};
  return typeof companyId === 'string' && typeof companySlug === 'string'
    ? { companyId, companySlug }
    : undefined;
}
